import { DocumentNode, OperationVariables, TypedDocumentNode, useMutation, useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import { builder } from './formBuilders'
import { get } from 'lodash'
import { CurrentUserQuery } from '@/core/current_user_query'
import { useRef } from 'react'

interface Props<Mutation, Attributes, Query, QueryRecord> {
  id?: string
  existingRecord?: string
  defaultValues?: Attributes
  initializer?: (node: QueryRecord) => Attributes
  query?: DocumentNode | TypedDocumentNode<Query, OperationVariables>
  mutation: DocumentNode | TypedDocumentNode<Mutation, OperationVariables>
  queryKey?: keyof Query
  queryVariables?: OperationVariables
  resultKey: keyof Mutation
  refetchQueries?: DocumentNode[]
}

interface ValidationError {
  path: string
  message: string
}

export const useForm = <Mutation, Attributes, Query, QueryRecord>({
  id,
  defaultValues,
  initializer,
  query,
  mutation,
  queryKey,
  queryVariables,
  resultKey,
  refetchQueries = []
}: Props<Mutation, Attributes, Query, QueryRecord>) => {
  const { data, loading: initQueryLoading, error: initQueryError } = useQuery<Query>(
    query || CurrentUserQuery,
    { variables: queryVariables || { filters: { idEq: id } }, skip: !query }
  )

  const [initializerCallback] = useState(() => initializer ?? (() => ({} as Attributes)))
  const [attributes, setAttributes] = useState<Attributes>(defaultValues || {} as Attributes)
  const attributesRef = useRef<Attributes>(null)
  attributesRef.current = attributes
  const [mutate] = useMutation<Mutation>(mutation, { refetchQueries })

  const [queryLoading, setQueryLoading] = useState(false)
  const [mutationLoading, setMutationLoading] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState<boolean>() // undef: no state, false: failed, true: succeeded
  const [fieldErrors, setFieldErrors] = useState<Record<keyof Attributes, string>>()
  const [queryError, setQueryError] = useState<string>()
  const [mutationError, setMutationError] = useState<string>()
  const [mutationData, setMutationData] = useState<Mutation | null>()

  const formBuilder = builder<Attributes>({ attributes, setAttributes, mutationError, fieldErrors })

  useEffect(() => setQueryLoading(initQueryLoading), [initQueryLoading])
  useEffect(() => setQueryError(initQueryError?.message), [initQueryError])

  useEffect(() => {
    if (!data || !queryKey || initQueryLoading) return

    // allowing any here because it should be the actual object returned and passing that in explicitly seemed to be
    // overly complicated compared to a cast
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setAttributes(initializerCallback((data[queryKey] as any)?.nodes?.[0] as QueryRecord))
  }, [data, initQueryLoading, initializerCallback, queryKey, setAttributes])

  const save = async () => {
    setMutationLoading(true)
    try {
      const response = await mutate({ variables: { id, attributes: attributesRef.current } })

      const error = response.errors?.[0].message
      const validationErrors = {} as Record<keyof Attributes, string>;
      (get(response.data, resultKey)?.errors as Array<ValidationError> || [])
        .forEach((e: ValidationError) => validationErrors[e.path as keyof Attributes] = e.message)

      if (!mutationError && Object.keys(validationErrors).length == 0) {
        setMutationData(response.data)
        setSaveSuccess(true)
      } else {
        setSaveSuccess(false)
      }

      setTimeout(() => {
        setSaveSuccess(undefined)
      }, 1500)

      setFieldErrors(validationErrors)
      setMutationError(error)
    } catch (e) {
      if (typeof e === 'string') {
        setMutationError(e)
      } else if (e instanceof Error) {
        setMutationError(e.message)
      }
    } finally {
      setMutationLoading(false)
    }
  }

  return {
    attributes,
    setAttributes,
    save,
    saveSuccess,
    formBuilder,
    fieldErrors,
    queryError,
    queryLoading,
    queryData: data,
    mutationLoading,
    mutationData,
  }
}
